// Copyright 2025, University of Maryland, All Rights Reserved
import { getWorkspacesApi } from './api';
import type { OrganizationResponse } from '../types/workspace';

export type OrganizationApi = ReturnType<typeof useOrganizationApi>;

// Hook-based API for internal use
export const useOrganizationApi = () => {
  const apiInstance = getWorkspacesApi();

  return {
    listOrganizations: async (): Promise<OrganizationResponse[]> => {
      console.log('Fetching organizations');
      const response = await apiInstance.get<OrganizationResponse[]>(
        `/organizations`
      );
      console.log('Fetched organizations:', response.data);
      return response.data;
    },

    createOrganization: async (data: { org_id?: string; name: string; description?: string }): Promise<OrganizationResponse> => {
      console.log('Creating organization:', data);
      const response = await apiInstance.post<OrganizationResponse>(
        `/admin/organizations`,
        data
      );
      console.log('Created organization:', response.data);
      return response.data;
    },

    getOrganization: async (org_id: string): Promise<OrganizationResponse> => {
      console.log('Fetching organization:', org_id);
      const response = await apiInstance.get<OrganizationResponse>(
        `/organizations/${org_id}`
      );
      console.log('Fetched organization:', response.data);
      return response.data;
    },

    updateOrganization: async (org_id: string, data: { name?: string; description?: string }): Promise<OrganizationResponse> => {
      console.log('Updating organization:', org_id, data);
      const response = await apiInstance.put<OrganizationResponse>(
        `/admin/organizations/${org_id}`,
        data
      );
      console.log('Updated organization:', response.data);
      return response.data;
    },

    deleteOrganization: async (org_id: string): Promise<void> => {
      console.log('Deleting organization:', org_id);
      await apiInstance.delete(`/admin/organizations/${org_id}`);
      console.log('Deleted organization');
    }
  };
};

// Standalone API functions for use outside React components
export const fetchOrganizations = async (): Promise<OrganizationResponse[]> => {
  const apiInstance = getWorkspacesApi();
  try {
    const response = await apiInstance.get<OrganizationResponse[]>(`/organizations`);
    return response.data;
  } catch (error) {
    console.error('[Workspaces API] Error fetching organizations:', error);
    throw error;
  }
};
